import { NodeViewWrapper } from "@tiptap/react"

import type { NodeViewProps } from "@tiptap/react"

import { InlineSessionImage } from "@/features/chat/components/inline-session-image"
import {
  getBasenameFromPath,
  stripSandboxPrefix,
  tryDecodeUriComponent,
} from "@/features/chat/components/inline-session-image-utils"

import { useCanvasStore } from "../stores/canvas-store"

function isSessionImage(src: string, filenames: string[]): boolean {
  if (!src || filenames.length === 0) return false

  const decodedSrc = tryDecodeUriComponent(src)
  const normalized = stripSandboxPrefix(decodedSrc)

  // Fast path: direct inclusion match
  for (const filename of filenames) {
    if (normalized.includes(filename)) return true
  }

  // Fallback: match by basename when LLM emits sandbox paths
  const basename = getBasenameFromPath(decodedSrc)
  return Boolean(basename && filenames.includes(basename))
}

export function SessionImageNodeView({ node }: NodeViewProps) {
  const sessionId = useCanvasStore((state) => state.sessionId)
  const sessionFilenames = useCanvasStore((state) => state.sessionFilenames)
  const imageContentBaseUrl = useCanvasStore((state) => state.imageContentBaseUrl)

  const rawSrc = String(node.attrs.src ?? "")
  const alt = typeof node.attrs.alt === "string" ? node.attrs.alt : undefined

  // A saved document serves images from its own storage (document base) even
  // without an active session; a live canvas uses the session id.
  const hasImageSource = Boolean(sessionId) || Boolean(imageContentBaseUrl)
  const shouldRenderAsSessionImage = hasImageSource && isSessionImage(rawSrc, sessionFilenames)

  return (
    <NodeViewWrapper contentEditable={false}>
      {shouldRenderAsSessionImage ? (
        <InlineSessionImage
          alt={alt}
          className="max-w-full"
          contentBaseUrl={imageContentBaseUrl ?? undefined}
          filenames={sessionFilenames}
          sessionId={sessionId ?? ""}
          src={rawSrc}
        />
      ) : (
        <img alt={alt} className="my-3 max-w-full rounded-xl" loading="lazy" src={rawSrc} />
      )}
    </NodeViewWrapper>
  )
}
